import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Play, Settings, Trash2, ExternalLink, LogOut, Copy, Users, ShieldCheck, Download, Upload, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_LABELS = {
  soccer: 'Soccer',
  rugby: 'Rugby',
  gaelic_football: 'Gaelic Football',
  hurling: 'Hurling / Camogie'
};

const Dashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [sports, setSports] = useState({});
  const [teams, setTeams] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [competitionDialogOpen, setCompetitionDialogOpen] = useState(false);
  const [newCompetition, setNewCompetition] = useState({ name: '', sport: 'soccer', logo_url: '' });
  const importFileRef = useRef(null);
  const [newGame, setNewGame] = useState({
    sport: 'soccer',
    competition_name: '',
    competition_id: '',  // Selected competition ID or 'custom'
    venue: '',
    match_date: '',
    match_time: '',
    watch_link: '',
    team1_name: 'Home',
    team2_name: 'Away',
    team1_id: '',  // Database team ID (optional)
    team2_id: '',  // Database team ID (optional)
    timer_direction: 'up',
    game_duration: 90
  });

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games`, {
        headers: getAuthHeaders()
      });
      setGames(response.data);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: getAuthHeaders()
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to load teams');
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`, {
        headers: getAuthHeaders()
      });
      setCompetitions(response.data);
    } catch (error) {
      console.error('Failed to load competitions');
    }
  };

  const fetchSports = async () => {
    try {
      const response = await axios.get(`${API}/sports`);
      setSports(response.data);
    } catch (error) {
      console.error('Failed to load sports config');
    }
  };

  useEffect(() => {
    fetchGames();
    fetchSports();
    fetchTeams();
    fetchCompetitions();
  }, []);

  const handleCreateGame = async () => {
    try {
      const sportConfig = sports[newGame.sport];
      
      // Determine competition name - either from selected preset or custom input
      let competitionName = newGame.competition_name;
      if (newGame.competition_id && newGame.competition_id !== 'custom') {
        const selectedComp = competitions.find(c => c.id === newGame.competition_id);
        if (selectedComp) {
          competitionName = selectedComp.name;
        }
      }
      
      const response = await axios.post(`${API}/games`, {
        sport: newGame.sport,
        competition_name: competitionName,
        venue: newGame.venue,
        match_date: newGame.match_date,
        match_time: newGame.match_time,
        watch_link: newGame.watch_link,
        team1: { 
          name: newGame.team1_name, 
          score: 0, 
          goals: 0, 
          points: 0, 
          yellow_cards: 0,
          black_cards: 0,
          red_cards: 0, 
          player_notice: '',
          team_db_id: newGame.team1_id || null  // Link to database team
        },
        team2: { 
          name: newGame.team2_name, 
          score: 0, 
          goals: 0, 
          points: 0, 
          yellow_cards: 0,
          black_cards: 0,
          red_cards: 0, 
          player_notice: '',
          team_db_id: newGame.team2_id || null  // Link to database team
        },
        timer_direction: newGame.timer_direction,
        game_duration: newGame.game_duration || sportConfig?.default_duration || 90,
        stoppage_time: 0
      }, {
        headers: getAuthHeaders()
      });
      setGames([...games, response.data]);
      setCreateDialogOpen(false);
      setNewGame({
        sport: 'soccer',
        competition_name: '',
        competition_id: '',
        venue: '',
        match_date: '',
        match_time: '',
        watch_link: '',
        team1_name: 'Home',
        team2_name: 'Away',
        team1_id: '',
        team2_id: '',
        timer_direction: 'up',
        game_duration: 90
      });
      toast.success('Game created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    }
  };

  const handleCreateCompetition = async () => {
    if (!newCompetition.name.trim()) {
      toast.error('Competition name is required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/competitions`, newCompetition, {
        headers: getAuthHeaders()
      });
      setCompetitions([...competitions, response.data]);
      setNewCompetition({ name: '', sport: 'soccer', logo_url: '' });
      setCompetitionDialogOpen(false);
      toast.success('Competition saved!');
    } catch (error) {
      toast.error('Failed to save competition');
    }
  };

  const handleSeedDefaults = async (sport) => {
    try {
      const response = await axios.post(
        `${API}/competitions/seed-defaults?sport=${sport}`,
        null,
        { headers: getAuthHeaders() }
      );
      const inserted = response.data.inserted || 0;
      if (inserted === 0) {
        toast.info(`${SPORT_LABELS[sport]} defaults already exist`);
      } else {
        setCompetitions(prev => [...prev, ...(response.data.competitions || [])]);
        toast.success(`Added ${inserted} ${SPORT_LABELS[sport]} competitions`);
      }
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const handleExportCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions/export`, {
        headers: getAuthHeaders()
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `competitions-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${response.data.count} competitions`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleImportCompetitions = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const response = await axios.post(`${API}/competitions/import`, payload, {
        headers: getAuthHeaders()
      });
      const { inserted = 0, skipped = 0 } = response.data;
      await fetchCompetitions();
      toast.success(`Imported ${inserted} (skipped ${skipped} duplicates)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Invalid import file');
    } finally {
      event.target.value = '';
    }
  };

  const handleDeleteCompetition = async (compId) => {
    try {
      await axios.delete(`${API}/competitions/${compId}`, {
        headers: getAuthHeaders()
      });
      setCompetitions(competitions.filter(c => c.id !== compId));
      toast.success('Competition deleted');
    } catch (error) {
      toast.error('Failed to delete competition');
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    
    try {
      await axios.delete(`${API}/games/${gameId}`, {
        headers: getAuthHeaders()
      });
      setGames(games.filter(g => g.id !== gameId));
      toast.success('Game deleted');
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const copyOverlayUrl = (gameId) => {
    const url = `${window.location.origin}/overlay/${gameId}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(() => {
        toast.success('Overlay URL copied to clipboard!');
      }).catch(() => {
        window.prompt('Copy this URL:', url);
      });
    } else {
      window.prompt('Copy this URL:', url);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatGAAScore = (team, sport) => {
    if (sport === 'gaelic_football' || sport === 'hurling') {
      return `${team.goals || 0}-${team.points || 0} (${team.score || 0})`;
    }
    return team.score || 0;
  };

  return (
    <div className="min-h-screen bg-[#0F172A] p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <span className="text-xs font-body uppercase tracking-widest text-[#D4E5ED] bg-[#2A4A5C] px-3 py-1 rounded">
            NEW LOGICAL SPORTS
          </span>
          <h1 className="font-heading text-4xl text-white mt-2 tracking-wide">SCOREBOARD DASHBOARD</h1>
          <p className="text-slate-400 font-body mt-1">
            Welcome, {user?.name}
            {user?.role && (
              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
                user.role === 'admin' 
                  ? 'bg-blue-900/50 text-blue-300 border border-blue-700' 
                  : 'bg-slate-700 text-slate-300 border border-slate-600'
              }`}>
                {user.role.toUpperCase()}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          {user?.role === 'admin' && (
            <Button
              onClick={() => navigate('/admin')}
              className="bg-blue-700 hover:bg-blue-600 text-white font-body h-12 touch-target"
              data-testid="admin-panel-btn"
            >
              <ShieldCheck className="w-5 h-5 mr-2" />
              Admin
            </Button>
          )}
          <Button
            onClick={() => navigate('/teams')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-body h-12 touch-target"
          >
            <Users className="w-5 h-5 mr-2" />
            Teams
          </Button>
          {user?.role === 'admin' && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-game-btn"
                  className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body h-12 touch-target"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  New Game
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Create New Game</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 font-body">Sport</Label>
                  <Select value={newGame.sport} onValueChange={(v) => {
                    const sportConfig = sports[v];
                    setNewGame({...newGame, sport: v, game_duration: sportConfig?.default_duration || 90});
                  }}>
                    <SelectTrigger data-testid="sport-select" className="bg-[#0F172A] border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-600">
                      {Object.entries(SPORT_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key} className="text-white hover:bg-[#2A4A5C]">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-slate-300 font-body">Competition</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCompetitionDialogOpen(true)}
                      className="text-xs text-[#F97316] hover:text-[#EA580C] h-6 px-2"
                    >
                      + Add New
                    </Button>
                  </div>
                  <Select 
                    value={newGame.competition_id || 'custom'} 
                    onValueChange={(v) => {
                      if (v === 'custom') {
                        setNewGame({...newGame, competition_id: 'custom', competition_name: ''});
                      } else {
                        const comp = competitions.find(c => c.id === v);
                        if (comp) {
                          setNewGame({...newGame, competition_id: v, competition_name: comp.name});
                        }
                      }
                    }}
                  >
                    <SelectTrigger data-testid="competition-select" className="bg-[#0F172A] border-slate-600">
                      <SelectValue placeholder="Select or enter custom" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-600">
                      <SelectItem value="custom" className="text-slate-400 hover:bg-[#2A4A5C]">
                        -- Custom --
                      </SelectItem>
                      {competitions
                        .filter(c => !newGame.sport || c.sport === newGame.sport || c.sport === 'all')
                        .map((comp) => (
                        <SelectItem key={comp.id} value={comp.id} className="text-white hover:bg-[#2A4A5C]">
                          {comp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(newGame.competition_id === 'custom' || !newGame.competition_id) && (
                    <Input
                      data-testid="competition-name-input"
                      value={newGame.competition_name}
                      onChange={(e) => setNewGame({...newGame, competition_name: e.target.value})}
                      className="bg-[#0F172A] border-slate-600 text-white mt-2"
                      placeholder="Enter competition name..."
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Venue (Optional)</Label>
                    <Input
                      value={newGame.venue}
                      onChange={(e) => setNewGame({...newGame, venue: e.target.value})}
                      className="bg-[#0F172A] border-slate-600 text-white"
                      placeholder="e.g., Tibbetts Brook Park"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newGame.match_date}
                      onChange={(e) => setNewGame({...newGame, match_date: e.target.value})}
                      className="bg-[#0F172A] border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Time (Optional)</Label>
                    <Input
                      type="time"
                      value={newGame.match_time}
                      onChange={(e) => setNewGame({...newGame, match_time: e.target.value})}
                      className="bg-[#0F172A] border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Watch Link (QR Code)</Label>
                    <Input
                      value={newGame.watch_link}
                      onChange={(e) => setNewGame({...newGame, watch_link: e.target.value})}
                      className="bg-[#0F172A] border-slate-600 text-white"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Team 1</Label>
                    <Select 
                      value={newGame.team1_id || '_custom'} 
                      onValueChange={(v) => {
                        if (v === '_custom') {
                          setNewGame({...newGame, team1_id: '', team1_name: 'Home'});
                        } else {
                          const team = teams.find(t => t.id === v);
                          if (team) {
                            setNewGame({...newGame, team1_id: v, team1_name: team.name});
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                        <SelectValue placeholder="Select or type custom..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-slate-600 max-h-60">
                        <SelectItem value="_custom" className="text-slate-400 hover:bg-[#2A4A5C]">
                          ✏️ Custom Name...
                        </SelectItem>
                        {teams.filter(t => t.sport === newGame.sport).map((team) => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-[#2A4A5C]">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded" 
                                style={{ backgroundColor: team.primary_color }}
                              />
                              {team.name} ({team.players?.length || 0} players)
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!newGame.team1_id && (
                      <Input
                        data-testid="team1-name-input"
                        value={newGame.team1_name}
                        onChange={(e) => setNewGame({...newGame, team1_name: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white"
                        placeholder="Enter team name"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Team 2</Label>
                    <Select 
                      value={newGame.team2_id || '_custom'} 
                      onValueChange={(v) => {
                        if (v === '_custom') {
                          setNewGame({...newGame, team2_id: '', team2_name: 'Away'});
                        } else {
                          const team = teams.find(t => t.id === v);
                          if (team) {
                            setNewGame({...newGame, team2_id: v, team2_name: team.name});
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                        <SelectValue placeholder="Select or type custom..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-slate-600 max-h-60">
                        <SelectItem value="_custom" className="text-slate-400 hover:bg-[#2A4A5C]">
                          ✏️ Custom Name...
                        </SelectItem>
                        {teams.filter(t => t.sport === newGame.sport).map((team) => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-[#2A4A5C]">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded" 
                                style={{ backgroundColor: team.primary_color }}
                              />
                              {team.name} ({team.players?.length || 0} players)
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!newGame.team2_id && (
                      <Input
                        data-testid="team2-name-input"
                        value={newGame.team2_name}
                        onChange={(e) => setNewGame({...newGame, team2_name: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white"
                        placeholder="Enter team name"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Timer Direction</Label>
                    <Select value={newGame.timer_direction} onValueChange={(v) => setNewGame({...newGame, timer_direction: v})}>
                      <SelectTrigger data-testid="timer-direction-select" className="bg-[#0F172A] border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-slate-600">
                        <SelectItem value="up" className="text-white hover:bg-[#2A4A5C]">Count Up</SelectItem>
                        <SelectItem value="down" className="text-white hover:bg-[#2A4A5C]">Count Down</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-body">Duration (mins)</Label>
                    <Input
                      data-testid="game-duration-input"
                      type="number"
                      value={newGame.game_duration}
                      onChange={(e) => setNewGame({...newGame, game_duration: parseInt(e.target.value) || 90})}
                      className="bg-[#0F172A] border-slate-600 text-white"
                    />
                  </div>
                </div>
                <Button 
                  data-testid="confirm-create-game-btn"
                  onClick={handleCreateGame}
                  className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-body h-12 touch-target"
                >
                  Create Game
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
          <Button 
            variant="outline" 
            data-testid="logout-btn"
            onClick={logout}
            className="border-slate-600 text-slate-300 hover:bg-slate-800 font-body h-12 touch-target"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Games Grid */}
      {loading ? (
        <div className="text-center text-slate-400 py-20">Loading games...</div>
      ) : games.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-slate-400 font-body text-lg mb-4">No active games yet</p>
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Game
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => (
            <Card 
              key={game.id} 
              data-testid={`game-card-${game.id}`}
              className="bg-[#1E293B] border-slate-700 hover:border-[#2A4A5C] transition-colors"
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xs font-body uppercase tracking-widest text-[#D4E5ED]">
                      {SPORT_LABELS[game.sport]}
                    </span>
                    {game.competition_name && (
                      <p className="text-slate-400 text-sm font-body mt-1">{game.competition_name}</p>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-body ${game.is_running ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-slate-700 text-slate-400'}`}>
                    {game.is_running ? 'LIVE' : 'PAUSED'}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Score Display */}
                <div className="bg-[#0F172A] rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div className="text-center flex-1">
                      <p className="font-heading text-lg text-white truncate">{game.team1.name}</p>
                      <p className="font-mono text-4xl text-[#D4E5ED] font-bold">
                        {formatGAAScore(game.team1, game.sport)}
                      </p>
                    </div>
                    <div className="px-4">
                      <p className="font-mono text-2xl text-slate-500">-</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="font-heading text-lg text-white truncate">{game.team2.name}</p>
                      <p className="font-mono text-4xl text-[#D4E5ED] font-bold">
                        {formatGAAScore(game.team2, game.sport)}
                      </p>
                    </div>
                  </div>
                  <div className="text-center mt-2">
                    <span className="font-mono text-xl text-slate-400">{formatTime(game.current_time)}</span>
                    {game.stoppage_time > 0 && (
                      <span className="text-[#F97316] font-mono ml-2">+{game.stoppage_time}'</span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid={`control-btn-${game.id}`}
                    onClick={() => navigate(`/control/${game.id}`)}
                    className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body h-12 touch-target"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Control
                  </Button>
                  <Button
                    data-testid={`copy-overlay-btn-${game.id}`}
                    variant="outline"
                    onClick={() => copyOverlayUrl(game.id)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 font-body h-12 touch-target"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Overlay URL
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    data-testid={`preview-overlay-btn-${game.id}`}
                    variant="outline"
                    onClick={() => window.open(`/overlay/${game.id}`, '_blank')}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 font-body h-12 touch-target"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  {user?.role === 'admin' && (
                    <Button
                      data-testid={`delete-btn-${game.id}`}
                      variant="outline"
                      onClick={() => handleDeleteGame(game.id)}
                      className="border-red-900 text-red-400 hover:bg-red-900/20 font-body h-12 touch-target"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* JSON Feed Info */}
      <div className="mt-12 p-6 bg-[#1E293B] rounded-lg border border-slate-700">
        <h2 className="font-heading text-xl text-white mb-2">JSON Feed for Embedding</h2>
        <p className="text-slate-400 font-body text-sm mb-3">
          Use these endpoints to embed live scores on your website:
        </p>
        <div className="space-y-2 font-mono text-sm">
          <div className="bg-[#0F172A] p-3 rounded flex items-center justify-between">
            <span className="text-[#D4E5ED]">{process.env.REACT_APP_BACKEND_URL}/api/feed/games</span>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/feed/games`;
                if (navigator.clipboard && window.isSecureContext) {
                  navigator.clipboard.writeText(url).then(() => {
                    toast.success('Copied!');
                  }).catch(() => {
                    window.prompt('Copy this URL:', url);
                  });
                } else {
                  window.prompt('Copy this URL:', url);
                }
              }}
              className="text-slate-400 hover:text-white"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Competition Management Dialog */}
      <Dialog open={competitionDialogOpen} onOpenChange={setCompetitionDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-lg" data-testid="competition-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Manage Competitions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Quick actions: seed defaults / import / export */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSeedDefaults(newCompetition.sport)}
                className="border-blue-700/50 bg-blue-900/20 text-blue-300 hover:bg-blue-900/40 hover:text-white"
                data-testid="seed-defaults-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Seed {SPORT_LABELS[newCompetition.sport]} defaults
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCompetitions}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="export-competitions-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => importFileRef.current?.click()}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="import-competitions-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Import
              </Button>
              <input
                ref={importFileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={handleImportCompetitions}
                data-testid="import-competitions-file"
              />
            </div>

            {/* Add new competition */}
            <div className="space-y-3 p-4 bg-[#0F172A] rounded-lg">
              <Label className="text-slate-300 font-body">Add New Competition</Label>
              <Input
                value={newCompetition.name}
                onChange={(e) => setNewCompetition({...newCompetition, name: e.target.value})}
                className="bg-[#1E293B] border-slate-600 text-white"
                placeholder="e.g., Premier League"
                data-testid="competition-name-input"
              />
              <Input
                value={newCompetition.logo_url}
                onChange={(e) => setNewCompetition({...newCompetition, logo_url: e.target.value})}
                className="bg-[#1E293B] border-slate-600 text-white"
                placeholder="Logo URL (optional) — https://..."
                data-testid="competition-logo-input"
              />
              <div className="flex gap-2">
                <Select 
                  value={newCompetition.sport} 
                  onValueChange={(v) => setNewCompetition({...newCompetition, sport: v})}
                >
                  <SelectTrigger className="bg-[#1E293B] border-slate-600 flex-1" data-testid="competition-sport-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-slate-600">
                    {Object.entries(SPORT_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-[#2A4A5C]">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleCreateCompetition}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="add-competition-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* List existing competitions */}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              <Label className="text-slate-400 font-body text-sm">Your Saved Competitions ({competitions.length})</Label>
              {competitions.length === 0 ? (
                <p className="text-slate-500 text-sm py-4 text-center">No competitions saved yet</p>
              ) : (
                competitions.map((comp) => (
                  <div 
                    key={comp.id} 
                    className="flex items-center justify-between p-3 bg-[#0F172A] rounded-lg"
                    data-testid={`competition-row-${comp.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {comp.logo_url ? (
                        <img
                          src={comp.logo_url}
                          alt=""
                          className="w-8 h-8 rounded object-contain bg-white/5 shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-slate-700/50 shrink-0 flex items-center justify-center text-slate-500 text-xs font-bold">
                          {comp.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-white font-body truncate">{comp.name}</p>
                        <p className="text-slate-500 text-xs">{SPORT_LABELS[comp.sport]}</p>
                      </div>
                    </div>
                    {user?.role === 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCompetition(comp.id)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-900/20 shrink-0"
                        data-testid={`delete-competition-${comp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
